import { get, isEmpty } from 'lodash';
import { observer } from 'mobx-react';
import { useRootStore } from '@nandosaus/state-management';
import { useRouter } from 'next/router';
import Head from 'next/head';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import styled from 'styled-components';
import { useLoadPastOrders } from '../../hooks/use-load-past-orders';
import { useLoadActiveCateringOrders } from '../../hooks/use-load-active-catering-orders';

import { analytics } from '../../analytics';
import { Box, Flex } from '../grid';
import { ErrorBoundary } from '../error-boundary';
import { floodlightPageLoadEventPropTypes } from '../../prop-types';
import { Footer } from '../footer';
import { Header } from '../header';
import { PageAlert } from '../page-alert';
import { PageProvider } from '../page-context';
import { ScreenButton } from '../screen-button';
import { seoPropDefaults, seoPropTypes } from '../../utils/extract-seo-meta-from-entry';
import { useFeature } from '../../hooks/use-feature';
import { useNavPadding } from '../../hooks/use-nav-padding';
import { OrderInProgressBanner } from '../order-in-progress-banner';

const defaultRouter = { pathname: null };

const Layout = ({
  title,
  metaContent,
  onScreenButtonProps,
  children,
  floodlightPageLoadEvent,
  hasFooterAcknowledgement,
  backgroundColor,
  noFooterMargin,
  hasNavPadding,
  hasTopBanner,
}) => {
  const { MemberStore, AuthStore, UIStore, PastOrdersStore } = useRootStore();
  const { profile } = MemberStore;

  const router = useRouter() || defaultRouter;

  // Enable header animation only on homepage
  const isHomepage = router.pathname === '/';

  // Default to dark navbar, some pages (e.g. homepage) will override to light in their own useEffect
  useEffect(() => {
    UIStore.setNavbarDark();
  }, [UIStore, router.pathname]);

  useEffect(() => {
    // @NOTE: lock the user to sign in page
    if (router.pathname !== '/sign-in' && profile && !profile.mobile) {
      router.replace('/sign-in');
    }
  }, [profile, router, router.pathname]);

  useEffect(() => {
    if (floodlightPageLoadEvent) {
      analytics.plugins['google-analytics'].trackFloodlightEvent(floodlightPageLoadEvent);
    }
  }, [floodlightPageLoadEvent]);

  const { additionalLinkTags, additionalMetaTags, jsonLinkedData, metaTitleOverride } = metaContent;
  const pageTitle = get(metaTitleOverride, '[0].data.title', title);

  const maintenanceMode = useFeature('maintenance');

  const { deliveryBannerActive, regularDeliveryActiveOrderCount, cateringDeliveryActiveOrderCount } = PastOrdersStore;
  const { mobileNavPadding, desktopNavPadding } = useNavPadding();

  // The delivery banner (OrderInProgressBanner) is absolutely positioned at the
  // very top. When an in-flow top banner (the S&D promo strip) is also shown, it
  // would render underneath the delivery banner. Offset the page content down by
  // the delivery banner's height (56px) so the top banner sits below it. Pages
  // that use hasNavPadding already account for the delivery banner via useNavPadding.
  const topBannerClearsDeliveryBanner = hasTopBanner && deliveryBannerActive;

  let wrapperPaddingTop;
  if (hasNavPadding) {
    wrapperPaddingTop = { xs: mobileNavPadding, lg: desktopNavPadding };
  } else if (topBannerClearsDeliveryBanner) {
    wrapperPaddingTop = '56px';
  }

  useLoadPastOrders();
  useLoadActiveCateringOrders();
  return (
    <Wrapper pt={wrapperPaddingTop} backgroundColor={backgroundColor}>
      <PageProvider title={title}>
        <Head>
          <title>{pageTitle}</title>
          <meta charSet="utf-8" />
          <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
          <meta name="theme-color" content="#000000" />
          <link
            rel="preload"
            href="/static/fonts/nandos-hand-new.woff2"
            as="font"
            type="font/woff2"
            crossOrigin="anonymous"
          />
          <link
            rel="preload"
            href="/static/fonts/Barlow-Regular.woff2"
            as="font"
            type="font/woff2"
            crossOrigin="anonymous"
          />
          <link
            rel="preload"
            href="/static/fonts/Barlow-Italic.woff2"
            as="font"
            type="font/woff2"
            crossOrigin="anonymous"
          />
          <meta name="viewport" content="initial-scale=1.0, width=device-width, viewport-fit=cover" key="viewport" />
          <meta property="og:title" content={title} />

          {!isEmpty(additionalMetaTags) && additionalMetaTags.map(tag => <meta key={tag.key} {...tag.data} />)}
          {!isEmpty(additionalLinkTags) && additionalLinkTags.map(tag => <link key={tag.key} {...tag.data} />)}
          {!isEmpty(jsonLinkedData) &&
            jsonLinkedData.map(data => (
              <script
                key={data.key}
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(data.data) }} // eslint-disable-line react/no-danger
              />
            ))}

          <link rel="shortcut icon" type="image/x-icon" href="/static/favicon.ico" />
        </Head>

        {!maintenanceMode ? (
          <>
            {deliveryBannerActive && (
              <OrderInProgressBanner
                regularOrderCount={regularDeliveryActiveOrderCount}
                cateringOrderCount={cateringDeliveryActiveOrderCount}
              />
            )}
            <Header
              profile={profile}
              deliveryBannerActive={deliveryBannerActive}
              topBannerActive={hasTopBanner}
              handleSignOut={AuthStore.signOut}
              enableAnimation={isHomepage}
              dark={UIStore.isNavbarLight}
            />
            <Main>
              <ErrorBoundary asPage>{children}</ErrorBoundary>
            </Main>
            <Footer
              hasAcknowledgement={hasFooterAcknowledgement}
              mt={noFooterMargin ? 0 : { xs: '32px', lg: '64px' }}
            />
            <ScreenButton {...onScreenButtonProps} />
            <PageAlert />
          </>
        ) : (
          <Flex justifyContent="center" alignItems="center" height="100vh">
            <Box>
              Unfortunately we’re offline at the moment performing critical maintenance. We&apos;ll back back ASAP!
            </Box>
          </Flex>
        )}
      </PageProvider>
    </Wrapper>
  );
};

const Wrapper = styled(Flex)`
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const Main = styled.main`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  background-size: cover;
`;

Layout.propTypes = {
  children: PropTypes.node.isRequired,
  floodlightPageLoadEvent: floodlightPageLoadEventPropTypes,
  metaContent: PropTypes.shape(seoPropTypes),
  onScreenButtonProps: PropTypes.shape({
    itemCount: PropTypes.number,
    onClick: PropTypes.func,
    price: PropTypes.string,
  }),
  title: PropTypes.string.isRequired,
  hasFooterAcknowledgement: PropTypes.bool,
  backgroundColor: PropTypes.string,
  noFooterMargin: PropTypes.bool,
  hasNavPadding: PropTypes.bool,
  hasTopBanner: PropTypes.bool,
};

Layout.defaultProps = {
  floodlightPageLoadEvent: undefined,
  metaContent: seoPropDefaults,
  onScreenButtonProps: null,
  hasFooterAcknowledgement: false,
  backgroundColor: 'warmWhite2',
  noFooterMargin: false,
  hasNavPadding: false,
  hasTopBanner: false,
};

const ObservedLayout = observer(Layout);
export { ObservedLayout as Layout };
